import {
    PaymentPlace,
    useAuthManager,
    TarifficatorPurchaseSuccessWidgetEvent,
    usePaymentPlace,
} from '@funtech-int/yango-shared';
import React, { FC, Suspense } from 'react';
import { SportEventState, SportViewOption } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { ErrorBoundary } from '@funtech-int/rival-react';

import { Button } from '@/shared/components/Button/Button';
import PlayIcon from '@/shared/assets/MoviePlay';
import { usePaymentWidget } from '@/shared/hooks/usePaymentWidget';
import { useOfferWebAnalytics } from '@/shared/hooks/useOfferWebAnalytics';
import { useSubscriptionRefetch } from '@/shared/hooks/useSubscriptionRefetch';
import { usePaymentPage } from '@/shared/hooks/usePaymentPage';
import { PlusPaymentPlace } from '@/shared/constants/payments';
import { usePaymentIntegrations } from '@/shared/hooks/usePaymentIntegrations';

import { useI18n } from '../../hooks/useI18n';
import { getOfferButtonText } from '../../lib/utils';

import styles from './EventContentAction.module.css';

export type EventContentActionValue = 'Offer' | 'Watch' | 'About' | 'Login';

interface EventContentActionProps {
    permittedActions: Set<EventContentActionValue>;
    loginUrl: URL;
    eventState: SportEventState;
    plusPaymentPlace: PlusPaymentPlace;
    viewOption: SportViewOption | undefined;
    onOfferClick?: () => void;
    onWatch?: () => void;
    onAbout?: () => void;
}

export const EventContentAction: FC<EventContentActionProps> = (props) => {
    const {
        loginUrl,
        viewOption,
        eventState,
        onOfferClick,
        onAbout,
        onWatch,
        permittedActions,
        plusPaymentPlace,
    } = props;

    const { isAuthorized } = useAuthManager();
    const i18n = useI18n();

    const billingFeature = viewOption?.billingFeatureNameToBuy;

    if (!isAuthorized && permittedActions.has('Login')) {
        return (
            <Button
                as="a"
                variant="accent"
                size={60}
                data-test-id="SportPromo_login"
                href={loginUrl.toString()}
            >
                {i18n('login_button_text')}
            </Button>
        );
    }

    if (!viewOption?.isPurchased && billingFeature && permittedActions.has('Offer')) {
        return (
            <OfferContainer
                viewOption={viewOption}
                eventState={eventState}
                plusPaymentPlace={plusPaymentPlace}
                onWatch={onWatch}
                onOfferClick={onOfferClick}
            />
        );
    }

    if (eventState === 'LIVE' && permittedActions.has('Watch')) {
        return (
            <Button
                name="Watch"
                variant="accent"
                size={60}
                startIcon={<PlayIcon fill="#fff" />}
                data-test-id="SportPromo_watch"
                onClick={onWatch}
            >
                {i18n('watch_text')}
            </Button>
        );
    }

    if (permittedActions.has('About')) {
        return (
            <Button
                name="About"
                variant="accentWhite"
                size={60}
                data-test-id="SportPromo_about"
                onClick={onAbout}
            >
                {i18n('event_button_text')}
            </Button>
        );
    }
};

interface OfferProps {
    paymentPlace: PaymentPlace;
    eventState: SportEventState;
    onWatch?: () => void;
    onOfferClick?: () => void;
}

const Offer: React.FC<OfferProps> = ({
    paymentPlace,
    eventState,
    onWatch,
    onOfferClick: onOfferClickCallback,
}) => {
    const paymentWidget = usePaymentWidget();
    const paymentIntegrations = usePaymentIntegrations();

    const offer = paymentPlace?.offers?.[0];
    const { purchaseSucceeded: sendSuccessPurchaseEvent } = useOfferWebAnalytics(offer);
    const { refetch: subscriptionRefetch } = useSubscriptionRefetch();

    if (!offer) {
        return null;
    }

    const onOfferShow = () => {
        if (offer) {
            paymentPlace?.analytics.offerShowed(offer);
        }
    };

    const onOfferClick = () => {
        onOfferClickCallback?.();
        paymentPlace?.analytics.offerClicked(offer);

        async function onPurchaseSuccess(event: TarifficatorPurchaseSuccessWidgetEvent) {
            await paymentIntegrations.submit(event);
            subscriptionRefetch();
            sendSuccessPurchaseEvent(event);
        }

        paymentWidget.open(paymentPlace, offer, {
            onPurchaseSuccess,
            onSuccessClose: () => {
                if (eventState === 'LIVE') {
                    onWatch?.();
                }
            },
        });
    };

    const buttonText = getOfferButtonText(offer, eventState);

    return (
        <div className={styles.offer}>
            <Button
                name="Offer"
                variant="plus"
                size={60}
                startIcon={<PlayIcon fill="#fff" />}
                data-test-id="SportOfferButton"
                onClick={onOfferClick}
                onShow={onOfferShow}
            >
                {buttonText}
            </Button>
            <div className={styles.offerCaption}>
                <span>{offer.offerTexts.subscriptionName}</span>
                <span>{offer.offerTexts.combinedFullOfferText}</span>
            </div>
        </div>
    );
};

interface OfferContainerProps {
    viewOption: SportViewOption;
    eventState: SportEventState;
    plusPaymentPlace: PlusPaymentPlace;
    onWatch?: () => void;
    onOfferClick?: () => void;
}

const OfferContainer: React.FC<OfferContainerProps> = (props) => {
    const { viewOption, plusPaymentPlace } = props;

    const page = usePaymentPage();
    const paymentPlace = usePaymentPlace({
        page,
        places: [plusPaymentPlace],
        extraParams: {
            customProperties: {
                billingFeatureName: viewOption?.billingFeatureNameToBuy,
            },
        },
    });

    return (
        <ErrorBoundary fallback={() => null}>
            <Suspense fallback={null}>
                <Offer {...props} paymentPlace={paymentPlace} />
            </Suspense>
        </ErrorBoundary>
    );
};
